import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { FiArrowUpRight, FiMenu, FiX } from "react-icons/fi";
import { primaryNavigation } from "../data/siteNavigation";
import { components } from "../styles/designSystem";

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 px-3 py-2 md:px-6 md:py-3">
        <div
          className={`flex items-center justify-between rounded-2xl px-3 py-1.5 transition-all duration-300 md:px-4`}
        >
          {/* Logo */}
          <Link
            to="/"
            className="group relative flex items-center"
            aria-label="Go to homepage"
          >
            <img
              src="/images/brand/o3s-logo.png"
              alt="O3S logo"
              className="h-16 w-auto transition-transform duration-300 group-hover:scale-[1.03] sm:h-16 md:h-20 lg:h-24"
            />
          </Link>

          {/* Desktop navigation */}
          <nav className="hidden items-center gap-1 rounded-2xl bg-secondary p-1.5 shadow-lg lg:flex">
            {primaryNavigation.map((item) => (
              <NavLink
                key={item.label}
                to={item.to}
                className={({ isActive }) =>
                  `rounded-xl px-4 py-2 text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? "bg-white text-secondary shadow-sm"
                      : "text-white/70 hover:bg-white/10 hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Desktop CTA */}
          <Link
            to="/contact"
            className={`${components.button.small} hidden lg:inline-flex`}
          >
            Let's talk
            <FiArrowUpRight className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>

          {/* Mobile button */}
          <button
            type="button"
            className="group flex h-11 w-11 items-center justify-center rounded-2xl bg-secondary text-white shadow-md transition-all duration-300 hover:bg-primary lg:hidden"
            onClick={() => setMenuOpen(true)}
            aria-label="Open navigation menu"
            aria-expanded={menuOpen}
          >
            <FiMenu
              size={21}
              className="transition-transform duration-300 group-hover:scale-110"
            />
          </button>
        </div>
      </header>

      {/* Mobile menu overlay */}
      <div
        className={`fixed inset-0 z-[60] transition-opacity duration-300 lg:hidden ${
          menuOpen
            ? "pointer-events-auto opacity-100"
            : "pointer-events-none opacity-0"
        }`}
      >
        {/* Backdrop */}
        <button
          type="button"
          className="absolute inset-0 h-full w-full cursor-default bg-secondary/30 backdrop-blur-sm"
          onClick={closeMenu}
          aria-label="Close navigation menu"
        />

        {/* Panel */}
        <div
          className={`absolute inset-x-4 bottom-4 top-4 overflow-hidden rounded-[2rem] border border-white/70 bg-white p-6 shadow-2xl transition-all duration-500 ease-out ${
            menuOpen ? "translate-y-0 scale-100" : "-translate-y-8 scale-[0.97]"
          }`}
        >
          {/* Decorative shapes */}
          <div className="pointer-events-none absolute -right-14 -top-14 h-48 w-48 rounded-full bg-primary/[0.10] blur-3xl" />
          <div className="pointer-events-none absolute -bottom-12 -left-12 h-44 w-44 rotate-12 rounded-[2.5rem] bg-accent/[0.12]" />
          <div className="pointer-events-none absolute right-10 top-28 h-4 w-4 rounded-full bg-primary/40" />
          <div className="pointer-events-none absolute bottom-24 right-12 h-8 w-8 rounded-xl border-4 border-accent/30" />

          {/* Header row */}
          <div className="relative flex items-center justify-between">
            <Link to="/" onClick={closeMenu} aria-label="Go to homepage">
              <img
                src="/images/brand/o3s-logo.png"
                alt="O3S logo"
                className="h-12 w-auto sm:h-14"
              />
            </Link>

            <button
              type="button"
              className="flex h-11 w-11 items-center justify-center rounded-2xl bg-secondary text-white transition-colors hover:bg-primary"
              onClick={closeMenu}
              aria-label="Close navigation menu"
            >
              <FiX size={23} />
            </button>
          </div>

          {/* Nav links */}
          <nav className="relative mt-14 flex flex-col">
            {primaryNavigation.map((item, index) => (
              <NavLink
                key={item.label}
                to={item.to}
                onClick={closeMenu}
                className={({ isActive }) =>
                  `group flex items-center justify-between border-b border-gray-200/70 py-5 text-2xl font-semibold transition-all duration-300 ${
                    isActive
                      ? "text-primary"
                      : "text-secondary hover:text-primary"
                  }`
                }
                style={{
                  transitionDelay: menuOpen ? `${index * 55}ms` : "0ms",
                }}
              >
                <span>{item.label}</span>
                <FiArrowUpRight className="text-base opacity-0 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:opacity-100" />
              </NavLink>
            ))}
          </nav>

          {/* CTA */}
          <div className="relative mt-10">
            <Link
              to="/contact"
              onClick={closeMenu}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-6 py-4 text-base font-semibold text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-secondary hover:shadow-xl"
            >
              Start a project
              <FiArrowUpRight className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>

            <p className="mt-5 text-center text-xs text-gray-400">
              Digital studio for small businesses.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
